import { Router } from '@angular/router';
import { Component, OnInit, Input, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { FormEditBaseComponent } from '@components/form-edit-base.component';
import { UntypedFormBuilder, Validators, FormGroup, ValidationErrors, UntypedFormControl, UntypedFormArray } from '@angular/forms';
import * as moment from 'moment';

import { UiService } from '@services/ui.service';
import { Order } from '@classes/order/order';
import { OrdersService } from '@services/orders.service';
import { AuthService } from '@services/auth.service';
import { RoleName } from '@enums/role-name.enum';
import { UsersService } from '@services/users.service';
import { ConfirmValidMatcher } from '@classes/validations/confirm-valid-matcher';
import { Client } from '@classes/client';
import { ClientsService } from '@services/clients.service';
import { Site } from '@classes/site';
import { SitesService } from '@services/sites.service';
import { DictionaryValuesService } from '@services/dictionaryValues.service';
import { Address } from '@classes/address';
import { AddressesService } from '@services/addresses.service';
import { DictionaryValue } from '@classes/dictionary/dictionaryValue';
import { Observable } from 'rxjs';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatLegacyAutocomplete as MatAutocomplete, MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import { map, startWith } from 'rxjs/operators';
import dayjs from 'dayjs';
import { RemindersService } from '@services/reminders.service';
import { Reminder } from '@classes/reminder';


@Component({
  selector: 'app-order-edit',
  templateUrl: './order-edit.component.html',
  styleUrls: ['./order-edit.component.scss']
})
export class OrderEditComponent extends FormEditBaseComponent implements OnInit {
  @Input() order: Order;

  clients: Client[];
  sites: Site[];
  transportTypes: DictionaryValue[];
  urgencyLevels: DictionaryValue[];
  lots: DictionaryValue[];
  senders: Address[];
  receivers: Address[];
  reminder: Reminder;
  newReminder: Reminder;

  stringMaxLength = 50;
  commentMaxLength = 500;
  isRestricted: boolean;
  isClient: boolean;
  isLoadingLots: boolean;
  mustCheckDate: boolean;
  confirmValidMatcher = new ConfirmValidMatcher();

  invalidFields: string[] = [];
  invalidMessage: string = '';

  isFtso: boolean;

  transportTypesSelected: DictionaryValue[] = [];
  devices: DictionaryValue[];
  filteredTransportTypes: Observable<DictionaryValue[]>;
  transportTypesCtrl = new UntypedFormControl();
  selectable = true;
  removable = true;
  addOnBlur = true;
  separatorKeysCodes: number[] = [ENTER, COMMA];

  @ViewChild('deviceInput') deviceInput: ElementRef<HTMLInputElement>;
  @ViewChild('auto') matAutocomplete: MatAutocomplete;
  allTransportTypes: any;

  constructor(
    protected uiSrv: UiService,
    protected fb: UntypedFormBuilder,
    private authSrv: AuthService,
    private usersSrv: UsersService,
    private clientsSrv: ClientsService,
    private ordersSrv: OrdersService,
    private sitesSrv: SitesService,
    private dictionarySrv: DictionaryValuesService,
    private addressesSrv: AddressesService,
    private remindersSrv: RemindersService,
    private router: Router
  ) {
    super(uiSrv, fb);
  }

  ngOnInit(): void {
    super.ngOnInit();
    // tslint:disable-next-line: max-line-length
    this.isRestricted = this.authSrv.getUser().role.name !== RoleName.super_admin && this.authSrv.getUser().role.name !== RoleName.bizzdev;
    this.isFtso = this.authSrv.getUser().role.name === RoleName.super_admin || this.authSrv.getUser().role.name === RoleName.bizzdev || this.authSrv.getUser().role.name === RoleName.admin || this.authSrv.getUser().role.name === RoleName.dispatcher;
    this.isClient = this.authSrv.getUser().role.name === RoleName.client;

    this.initSubscriptions();
  }

  isControlSet(control: string): boolean {
    return !!this.editForm.get(control);
  }

  onSubmit(): void {
    this.isLoading = true;
    this.update();
  }

  private update(): void {
    const formValues = this.editForm.value;

    const orderCopy: Order = JSON.parse(JSON.stringify(this.order));

    if (typeof formValues.sender[0].address === 'string') {
      formValues.sender[0].otherAddress = formValues.sender[0].address;
      formValues.sender[0].address = null;
    } else {
      formValues.sender[0].otherAddress = null;
    }

    formValues.receivers.map(receiver => {
      if (typeof receiver.address === 'string') {
        receiver.otherAddress = receiver.address;
        receiver.address = null;
      } else {
        receiver.otherAddress = null;
      }

      if (receiver.type === 'isDelivery') {
        receiver.isDelivery = true;
        receiver.isPickup = false;
      } else {
        receiver.isDelivery = false;
        receiver.isPickup = true;
      }
    })

    if (formValues.patients.length > 0) {
      formValues.patients.map((patient, index) => {
        if (patient !== null) {
          if (patient.firstname === null && patient.lastname === null && patient.birthname === null && patient.birthdate === null && patient.productType === null && patient.quantity === null) {
            formValues.patients[index] = null;
          }
        }
      });
    }


    formValues.patients = formValues.patients.filter(patient => patient !== null);

    let callDate = null;

    if (formValues.callDate) {
      let callTime = formValues.callDate.split(':');
      let dateNow = moment(new Date());
      callDate = dateNow.hours(callTime[0]).minutes(callTime[1]).utc().toDate();
    }

    orderCopy.site = formValues.site;
    orderCopy.urgency = formValues.urgencyLevel;
    orderCopy.startDate = this.scheduleToDate(formValues.schedule[0]);
    orderCopy.ordererPhone = formValues.ordererPhone;
    orderCopy.ordererComment = formValues.ordererComment;
    orderCopy.ordererName = formValues.ordererName;
    orderCopy.sender = formValues.sender[0];
    orderCopy.receivers = formValues.receivers;
    orderCopy.transportTypes = this.transportTypesSelected;
    orderCopy.patients = formValues.patients;
    orderCopy.transportVoucher = formValues.transportVoucher;
    orderCopy.callDate = callDate;
    orderCopy.lot = formValues.lot;

    if (formValues.schedule[0].recallTimer) {
      let recallTimer = dayjs(orderCopy.startDate, { utc: true }).subtract(formValues.schedule[0].recallTimer, 'minute').format();

      if (this.reminder) {
        let reminderToEdit = this.reminder;
        reminderToEdit.dateTime = recallTimer;
        this.newReminder = reminderToEdit;
      }
    }

    orderCopy.modifiedFields = this.checkWhatChanged(orderCopy);

    this.ordersSrv.put(orderCopy)
      .subscribe(() => {
        this.isLoading = false;

        if (formValues.schedule[0].recallTimer) {
          let recallTimer = dayjs(orderCopy.startDate, { utc: true }).subtract(formValues.schedule[0].recallTimer, 'minute').format();

          if (this.reminder) {
            let reminderToEdit = this.reminder;
            reminderToEdit.dateTime = recallTimer;
            this.newReminder = reminderToEdit;
            this.remindersSrv.put(reminderToEdit).subscribe();
          } else {
            this.remindersSrv.post(orderCopy.id, recallTimer).subscribe();
          }
        } else if (this.reminder) {
          this.remindersSrv.delete(this.reminder).subscribe();
        }


        this.onFinish();
        this.uiSrv.showSnackbar('orders.edit.edited', true);
      });
  }

  private initSubscriptions(): void {
    this.isLoading = true;

    const sitesPromise = new Promise((resolve, reject) => {
      this.sitesSrv.getList()
        .subscribe(sitesFromSrv => {
          this.sites = sitesFromSrv;
          resolve(true);
        });
    });

    const clientsPromise = new Promise((resolve, reject) => {
      this.clientsSrv.getList()
        .subscribe(clientsFromSrv => {
          this.clients = clientsFromSrv;
          resolve(true);
        });
    });

    const typeTransportsPromise = new Promise((resolve, reject) => {
      this.dictionarySrv.getTransportListById2(this.order.client.id)
        .subscribe(transportsFromSrv => {
          this.allTransportTypes = transportsFromSrv;
          resolve(true);
        });
    });

    const urgencyLevelsPromise = new Promise((resolve, reject) => {
      this.dictionarySrv.getUrgencyListById2(this.order.client.id)
        .subscribe(urgencyLevelsFromSrv => {
          this.urgencyLevels = urgencyLevelsFromSrv;
          this.urgencyLevels.map(urgency => {
            if (this.order.urgency.id === urgency.id) {
              urgency.isChecked = true;
            }
          })
          resolve(true);
        });
    });

    const lotsPromise = new Promise((resolve, reject) => {
      this.dictionarySrv.getLotsList(this.order.site.id)
        .subscribe(lotsFromSrv => {
          this.lots = lotsFromSrv;
          resolve(true);
        });
    });


    const reminderPromise = new Promise((resolve, reject) => {
      this.remindersSrv.getByOrderId(this.order.id)
        .subscribe(reminderFromSrv => {
          if (reminderFromSrv.length > 0) {
            this.reminder = reminderFromSrv[0];
          } else {
            this.reminder = null;
          }

          resolve(true);
        });
    });

    const addressPromise = new Promise((resolve, reject) => {
      this.addressesSrv.getList()
        .subscribe(addressessFromSrv => {
          this.senders = addressessFromSrv.filter(address => address.isSender);

          this.receivers = addressessFromSrv.filter(address => address.isReceiver);


          resolve(true);
        });
    });

    Promise.all([sitesPromise, clientsPromise, typeTransportsPromise, urgencyLevelsPromise, addressPromise, reminderPromise, lotsPromise])
      .then(() => {
        this.isLoading = false;
        this.initForm();
      });
  }


  private initForm(): void {
    let callDate = null;
    if (this.order.callDate) {
      callDate = moment(this.order.callDate).hours().toString() + ':' + moment(this.order.callDate).minutes().toString();
    }

    this.editForm = this.fb.group({
      ordererComment: [this.order.ordererComment, [Validators.maxLength(this.commentMaxLength)]],
      // transportTypes: this.fb.array([]),
      urgencyLevel: new UntypedFormControl(null, [Validators.required]),
      lot: new UntypedFormControl(this.order.lot),
      schedule: this.fb.array([]),
      sender: this.fb.array([], Validators.required),
      receivers: this.fb.array([], Validators.required),
      ordererPhone: new UntypedFormControl(this.order.ordererPhone, [Validators.maxLength(this.stringMaxLength)]),
      ordererName: new UntypedFormControl(this.order.ordererName, [Validators.maxLength(this.stringMaxLength)]),
      patients: this.fb.array([]),
      site: new UntypedFormControl(this.order.site, [Validators.required]),
      transportVoucher: new UntypedFormControl(this.order.transportVoucher, [Validators.maxLength(this.stringMaxLength)]),
      callDate: new UntypedFormControl(callDate),
    });

    if (this.lots.length > 0) {
      this.editForm.get('lot').setValidators([Validators.required]);
    }

    this.sites.map(site => {
      if (site.id === this.order.site.id) {
        this.editForm.get('site').setValue(site);
      }
    })

    this.urgencyLevels.map(urgency => {
      if (urgency.isChecked) {
        this.editForm.get('urgencyLevel').setValue(urgency);
      }
    })

    this.filteredTransportTypes = this.transportTypesCtrl.valueChanges.pipe(
      startWith(null),
      map((fruit: string | null) => fruit ? this._filter(fruit) : this.allTransportTypes.slice()));

    if (this.order.transportTypes) {
      this.order.transportTypes.map(transportType => {
        this.transportTypesSelected.push(transportType);
      })

      this.checkIfSelected();
    }

    this.onAddSender(this.order.sender);

    if (this.order.receivers) {
      this.order.receivers.map((receiver, index) => {
        this.onAddReceiver(receiver, index);
      })
    } else {
      this.onAddReceiver();
    }

    this.initDateForm();

    this.editForm.valueChanges.subscribe(() => {
      this.invalidFields = this.findInvalidControls();
      this.invalidMessage = 'Le formulaire est incomplet';
      this.invalidFields.map(field => {
        this.invalidMessage = this.invalidMessage + ' ' + field;
      })
    })

    this.editForm.get('site').valueChanges.subscribe((site) => {
      this.isLoadingLots = true;
      this.lots = [];
      this.editForm.get('lot').reset();
      if (typeof site !== 'string') {
        this.dictionarySrv.getLotsList(site.id)
          .subscribe(lotsFromSrv => {
            this.lots = lotsFromSrv;
            this.isLoadingLots = false;
          });
      }
    })
  }

  public findInvalidControls() {
    const invalid = [];
    const controls = this.editForm.controls;
    for (const name in controls) {
      if (controls[name].invalid) {
        invalid.push(this.translateFields(name));
      }
    }


    //check si le dernier receiver est bien une livraison
    let lastReceiver = this.editForm.value.receivers[this.editForm.value.receivers.length - 1];


    if (lastReceiver.type === 'isPickup') {
      invalid.push(' | La dernière étape est un enlèvement');
    }

    if (this.transportTypesSelected.length === 0) {
      invalid.push(' | Il n\'y a aucun type de transport');
    }

    return invalid;
  }

  translateFields(name): string {
    if (name === 'site') {
      return ' | Donneur d\'ordre';
    }
    if (name === 'urgencyLevel') {
      return ' | Niveau d\'urgence';
    }
    if (name === 'sender') {
      return ' | Lieu d\'enlèvement incomplet';
    }
    if (name === 'receivers') {
      return ' | Destinataire(s) incomplet(s)';
    }
    if (name === 'patients') {
      return ' | Patient(s) incomplet(s)';
    }
    if (name === 'lot') {
      return ' | Lot incomplet';
    }
  }

  minTransportType(min = 1): any {
    return (formArray: UntypedFormArray) => {
      return formArray.controls.length >= min ? null : { required: true };
    };
  }

  // onAddTransportType(transportType?: DictionaryValue): void {

  //   this.getArray('transportTypes').push(this.fb.control(transportType));
  // }

  removeTransportType(transportTypeToRemove): void {
    this.getArray('transportTypes').controls.splice(this.getArray('transportTypes').controls.indexOf(transportTypeToRemove), 1);
    this.editForm.value.transportTypes.splice(this.editForm.value.transportTypes.indexOf(transportTypeToRemove.value), 1);
  }

  get transportTypesFields(): UntypedFormArray {
    return this.editForm.get('transportTypes') as UntypedFormArray;
  }

  private scheduleToDate(schedule: any): Date {
    if (!schedule.startDate || !schedule.startTime || typeof schedule.startDate === 'string') {
      return null;
    }

    const startDate: moment.Moment = schedule.startDate.local();
    const startTime: number[] = schedule.startTime.split(':').map(s => parseInt(s, 10));
    return startDate.hours(startTime[0]).minutes(startTime[1]).utc().toDate();
  }

  initDateForm(): void {
    const startMoment: moment.Moment = moment(new Date(this.order.startDate));
    const startTime: string = moment(startMoment).format('HH:mm');
    let recallTimer = null;

    if (this.reminder) {
      const startDate = dayjs(this.order.startDate);
      const recallDate = dayjs(this.reminder.dateTime);
      recallTimer = startDate.diff(recallDate, "minute");
    }

    const newGroup = this.fb.group({
      startDate: [startMoment, Validators.required],
      startTime: [startTime, Validators.required],
      recallTimer: [recallTimer, Validators.min(0)]
    });

    this.getArray('schedule').push(newGroup);

    newGroup.controls.startDate.valueChanges
      .subscribe(() => {
        this.mustCheckDate = true;
      });

    newGroup.controls.startTime.valueChanges
      .subscribe(() => {
        this.mustCheckDate = true;
      });



  }

  resetStartDate(control) {
    control.get('startDate').reset();
  }

  resetBirthDate(control) {
    control.get('birthdate').reset();
  }

  onAddPatient(indexReceiver: number, patient?): void {
    let firstname = null;
    let birthname = null;
    let lastname = null;
    let birthdate = null;
    let productType = null;
    let quantity = null;
    let ipp = null;
    let id = null;

    if (patient) {
      firstname = patient.firstname;
      birthname = patient.birthname;
      lastname = patient.lastname;
      birthdate = patient.birthdate;
      productType = patient.productType;
      quantity = patient.quantity;
      ipp = patient.ipp;
      id = patient.id;
    }

    const newGroup = this.fb.group({
      firstname: [firstname, [Validators.maxLength(this.stringMaxLength)]],
      birthname: [birthname, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
      lastname: [lastname, [Validators.maxLength(this.stringMaxLength)]],
      birthdate: [birthdate, [Validators.required]],
      productType: [productType, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
      quantity: [quantity, [Validators.required]],
      ipp: [ipp, Validators.maxLength(this.stringMaxLength)],
      id: [id]
    });

    this.getArrayPatients(indexReceiver).push(newGroup);
  }

  onCopyPatient(indexReceiver: number, control) {
    let patient = control.value;

    const newGroup = this.fb.group({
      firstname: [patient.firstname, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
      birthname: [patient.birthname, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
      lastname: [patient.lastname, [Validators.maxLength(this.stringMaxLength)]],
      birthdate: [patient.birthdate, [Validators.required]],
      productType: [null, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
      quantity: [null, [Validators.required]],
      ipp: [null, Validators.maxLength(this.stringMaxLength)]
    });

    this.getArrayPatients(indexReceiver).push(newGroup);
  }

  getArrayPatients(index?: number) {
    let receivers = this.editForm.get('receivers') as UntypedFormArray;
    let patients = receivers.controls[index].get('patients') as UntypedFormArray;
    return patients;
  }

  onRemovePatient(indexReceiver: number, indexPatient: number): void {
    this.getArrayPatients(indexReceiver).removeAt(indexPatient);
  }

  isControlPatientSet(index: number): boolean {
    let receivers = this.editForm.get('receivers') as UntypedFormArray;

    return !!receivers.controls[index].get('patients');
  }

  onAddReceiver(receiver?, index?): void {
    let address = null;
    let otherAddress = null;
    let service = null;
    let ordererComment = null;
    let type = null;
    let id = null;

    if (receiver) {
      id = receiver.id;
      address = receiver.address;
      otherAddress = receiver.otherAddress;
      service = receiver.service;
      ordererComment = receiver.ordererComment;
      if (receiver.isPickup) {
        type = "isPickup";
      } else {
        type = "isDelivery";
      }
    }

    const newGroup = this.fb.group({
      id: [id],
      address: [address, [Validators.required]],
      otherAddress: [otherAddress],
      service: [service, [Validators.required]],
      ordererComment: [ordererComment, [Validators.maxLength(this.commentMaxLength)]],
      type: [type, [Validators.required]]
    });

    if (newGroup.get('type').value === 'isDelivery') {
      newGroup.addControl('patients', this.fb.array([]));
    }

    newGroup.get('type').valueChanges.subscribe(result => {
      if (result === 'isDelivery') {
        newGroup.addControl('patients', this.fb.array([]));
      } else {
        newGroup.removeControl('patients');
      }
    })

    if (receiver) {

      if (receiver.otherAddress) {
        newGroup.get('address').setValue(receiver.otherAddress);
        newGroup.get('otherAddress').setValue(receiver.otherAddress);
      }
      else {
        this.receivers.map(oneReceiver => {
          if (oneReceiver.id === receiver.address.id) {
            newGroup.get('address').setValue(oneReceiver);
          }
        })
      }
    }
    this.getArray('receivers').push(newGroup);

    if (receiver.patients) {
      receiver.patients.map(patient => {
        this.onAddPatient(index, patient);
      })
    }
  }

  onRemoveReceiver(index: number): void {
    this.getArray('receivers').removeAt(index);
  }

  onAddSender(sender?): void {
    let address = null;
    let otherAddress = null;
    let service = null;
    let ordererComment = null;
    let type = null;
    let id = null;

    if (sender) {
      id = sender.id;
      address = sender.address;
      otherAddress = sender.otherAddress;
      service = sender.service;
      ordererComment = sender.ordererComment;
    }

    const newGroup = this.fb.group({
      id: [id],
      address: [address, [Validators.required]],
      otherAddress: [otherAddress],
      service: [service, [Validators.required]],
      ordererComment: [ordererComment, [Validators.maxLength(this.commentMaxLength)]],
      isPickup: true,
      isDelivery: false
    });

    if (sender) {

      if (sender.otherAddress) {
        newGroup.get('address').setValue(sender.otherAddress);
        newGroup.get('otherAddress').setValue(sender.otherAddress);
      }
      else {
        this.senders.map(oneSender => {
          if (oneSender.id === sender.address.id) {
            newGroup.get('address').setValue(oneSender);
          }
        })
      }
    }
    this.getArray('sender').push(newGroup);
  }

  onCheckboxChange(e): any {
    const checkArray: UntypedFormArray = this.editForm.get('transportTypes') as UntypedFormArray;

    if (e.checked) {
      checkArray.push(new UntypedFormControl(e.source.value));
    } else {
      let i: number = 0;
      checkArray.controls.forEach((item: UntypedFormControl) => {
        if (item.value === e.source.value) {
          checkArray.removeAt(i);
          return;
        }
        i++;
      });
    }
  }

  onFinish(): void {
    this.editFinished.emit();
  }

  checkWhatChanged(orderCopy): string[] {
    let modifiedFields = [];

    if (this.order.site.id !== orderCopy.site.id) {
      modifiedFields.push('Donneur d\'ordre');
    }

    if (this.order.transportVoucher != orderCopy.transportVoucher) {
      modifiedFields.push('Numéro de bon de transport');
    }

    if (this.order.transportTypes.length !== orderCopy.transportTypes.length) {
      modifiedFields.push('Type de transport')
    } else {
      let hasChanged = false;
      orderCopy.transportTypes.map((transportType, index) => {
        if (this.order.transportTypes[index].id !== transportType.id) {
          hasChanged = true;
        }
      })

      if (hasChanged) {
        modifiedFields.push('Type de transport');
      }
    }

    if (this.order.urgency.id !== orderCopy.urgency.id) {
      modifiedFields.push('Niveau d\'urgence');
    }

    if (new Date(this.order.startDate).toString() !== new Date(orderCopy.startDate).toString()) {
      modifiedFields.push('Dates/heures d\'enlèvement');
    }

    if (this.order.sender.address) {
      if (orderCopy.sender.address) {
        if (this.order.sender.address.id !== orderCopy.sender.address.id) {
          modifiedFields.push('Adresse de l\'étape 1');
        }
      } else {
        modifiedFields.push('Adresse de l\'étape 1');
      }
    } else if (this.order.sender.otherAddress) {
      if (orderCopy.sender.otherAddress) {
        if (this.order.sender.otherAddress !== orderCopy.sender.otherAddress) {
          modifiedFields.push('Adresse de l\'étape 1');
        }
      } else {
        modifiedFields.push('Adresse de l\'étape 1');
      }
    }
    if (this.order.sender.service !== orderCopy.sender.service) {
      modifiedFields.push('Sevice de l\'étape 1')
    }

    if ((this.order.sender.ordererComment ?? null) !== (orderCopy.sender.ordererComment ?? null)) {
      modifiedFields.push('Commentaire de l\'étape 1');
    }

    if (this.order.ordererComment !== orderCopy.ordererComment) {
      modifiedFields.push('Commentaire sur la course');
    }

    if (this.order.ordererName !== orderCopy.ordererName) {
      modifiedFields.push('Nom du demandeur');
    }

    if (this.order.ordererPhone !== orderCopy.ordererPhone) {
      modifiedFields.push('Téléphone du demandeur');
    }

    if (this.order.receivers.length !== orderCopy.receivers.length) {
      modifiedFields.push('Étape(s)');
    } else {
      orderCopy.receivers.map((receiver, index) => {
        let oldReceiver = this.order.receivers[index];
        let position = index + 2;
        if (receiver.address) {
          if (oldReceiver.address) {
            if (receiver.address.id !== oldReceiver.address.id) {
              modifiedFields.push('Adresse de l\'étape ' + position);
            }

          } else {
            modifiedFields.push('Adresse de l\'étape ' + position);
          }
          if (receiver.service !== oldReceiver.service) {
            modifiedFields.push('Service de l\'étape ' + position);
          }
          if ((receiver.ordererComment ?? null) !== (oldReceiver.ordererComment ?? null)) {
            modifiedFields.push('Commentaire de l\'étape ' + position);
          }
        } else if (receiver.otherAddress) {
          if (oldReceiver.otherAddress) {
            if (receiver.otherAddress !== oldReceiver.otherAddress) {
              modifiedFields.push('Adresse de l\'étape ' + position);
            }

          } else {
            modifiedFields.push('Adresse de l\'étape ' + position);
          }
          if (receiver.service !== oldReceiver.service) {
            modifiedFields.push('Service de l\'étape ' + position);
          }
          if (receiver.ordererComment !== oldReceiver.ordererComment) {
            modifiedFields.push('Commentaire de l\'étape ' + position);
          }
        }
      })
    }

    if (this.order.patients.length !== orderCopy.patients.length) {
      modifiedFields.push('Liste patients');
    } else {
      orderCopy.patients.map((patient, index) => {
        let position = index + 1;
        if (patient.firstname != this.order.patients[index].firstname || patient.birthname != this.order.patients[index].birthname || patient.lastname != this.order.patients[index].lastname || new Date(patient.birthdate).toString() != new Date(this.order.patients[index].birthdate).toString() || patient.productType != this.order.patients[index].productType || patient.ipp != this.order.patients[index].ipp) {
          modifiedFields.push('Patient ' + position);
        }
      })
    }

    for (let i = 1; i < modifiedFields.length; i++) {
      modifiedFields[i] = " " + modifiedFields[i];
    }

    return modifiedFields;
  }

  remove(fruit: DictionaryValue): void {
    const index = this.transportTypesSelected.indexOf(fruit);

    if (index >= 0) {
      this.transportTypesSelected.splice(index, 1);
    }

    this.checkIfSelected();
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    document.getElementById("deviceChipList").blur();
    if (!this.transportTypesSelected.includes(event.option.value)) {
      this.transportTypesSelected.push(event.option.value);
      // this.devices = this._filter(event.option.value);
      this.checkIfSelected();

      this.deviceInput.nativeElement.value = '';
    }

  }

  checkIfSelected() {

    this.filteredTransportTypes = this.filteredTransportTypes.pipe(
      map(array => {
        return array.map(element => {
          element.selected = false;
          this.transportTypesSelected.map(selected => {
            if (selected.id === element.id) {
              element.selected = true;
            }
          })
          return element;
        })

      })
    )
  }

  private _filter(value?): DictionaryValue[] {
    let filterValue = '';

    if (value.name) {
      filterValue = value.name.toLowerCase();
      return this.allTransportTypes.filter(el => {
        return el.id === value.id;
      });
    } else {
      filterValue = value.toLowerCase();
      return this.allTransportTypes.filter(transportType => transportType.name.toLowerCase().indexOf(filterValue) === 0);
    }




  }

  clearDevicesSelected(): void {
    this.transportTypesSelected = [];
    this.filteredTransportTypes.pipe(
      map(fruit => {
        return this.allTransportTypes;
      })
    )
    this.checkIfSelected();
  }

}
